import * as THREE from 'three';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { createUnitAnimator } from './create-unit-animator.js';
import { createUnitHitArea } from './unit-picking.js';
import { groundDecal, groundUi } from '../environment/ground-decal.js';
import { VisibleUnitGroup, skipHiddenTransforms } from '../scene-transforms.js';
import { createSelectionGlyph } from './selection-glyph.js';
import { HERO_BAND, gaugeEdges, makeHealthGauge } from './health-gauge.js';
import { prepareImpactBounds } from '../combat/impact-bounds.js';
import { batchCharacterPalettes } from './character-palettes.js';
import { foldHeadShadows, trimFigureShadows } from './figure-shadows.js';
import { shareFigureMaterials } from './figure-materials.js';
import { foldFigureProps } from './prop-folding.js';
import { optimiseSettledCorpse } from './settled-corpse.js';
import { prepareDeathRagdolls } from './death-ragdoll.js';
import type { CharacterRig } from './character-model.js';
export function createUnitVisual(definition: UnitDefinition) {
  const group = new VisibleUnitGroup();
  const { animator, rig } = createUnitAnimator(definition); group.add(rig.root);
  batchCharacterPalettes(rig.root);
  // After batching, whose groups follow shadow casting and which leaves surfaces with their own depth material alone.
  trimFigureShadows(rig.root);
  shareFigureMaterials(rig.root);
  skipHiddenTransforms(rig.root);
  prepareImpactBounds(rig.root);
  // After the impact bounds, which keep the exact ray test for rigid parts on their own geometry.
  foldFigureProps(rig.root);
  foldHeadShadows(rig.root);
  optimiseSettledCorpse(rig.root, animator);
  const ready = prepareDeathRagdolls(), hero = 'playerSlot' in definition;
  const ring = groundUi(groundDecal(new THREE.Mesh(gaugeEdges(new THREE.RingGeometry(hero ? definition.radius * (1 - HERO_BAND) : Math.max(0, definition.radius - .05), definition.radius, 64), 0), new THREE.MeshBasicMaterial({ color: definition.color, side: THREE.DoubleSide, transparent: true, opacity: 0.65 }))));
  // Heroes drain continuously, their stroke following their state; enemies drain in their three condition tiers.
  makeHealthGauge(ring.material, { tiers: hero ? 0 : 3, outer: definition.radius, inner: ring.geometry.parameters.innerRadius, stroked: hero });
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.055; group.add(ring);
  const selectionGlyph = hero ? createSelectionGlyph(definition.radius, definition.color) : undefined;
  if (selectionGlyph) group.add(selectionGlyph);
  const hitArea = createUnitHitArea(definition.radius, rig.height); group.add(hitArea);
  group.position.set(definition.spawn.x, 0, definition.spawn.z);
  const label = document.createElement('div'); label.className = 'world-label'; label.style.color = `#${definition.color.toString(16).padStart(6, '0')}`; label.textContent = definition.name;
  // A torch goes in a human hand (`Torches` holds it); other bodies have no hand to hold one.
  const torchRig = definition.torch && 'leftArm' in rig ? rig as CharacterRig : undefined;
  return { group, figure: rig.root, torchRig, ring, selectionGlyph, hitArea, label, animator, ready, labelHeight: rig.height + .3 };
}
